/**
 * MT5 Execution Audit Trail — admin-only searchable log of every signal
 * event (enqueue, deliver, fill, reject, expire, override, cancel). Backed
 * by GET /api/mt5/admin/logs. Filter by user email, symbol, event, window.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  bg: "#0d1117", card: "#111722", border: "#1f2733", text: "#e6e7ec",
  muted: "#7f8792", gold: "#C9A227", green: "#22c55e", red: "#ef4444", blue: "#3b82f6",
};

const EVENT_COLOR = {
  ea_filled: C.green, executed_from_setup: C.blue, enqueued: C.blue,
  approved: C.green, delivered_to_ea: C.gold, ea_rejected: C.red,
  enqueue_rejected: C.red, expired: C.muted, cancelled: C.muted,
  order_type_override: C.gold, report_ignored_final: C.muted,
};

const inp = {
  background: C.bg, border: `1px solid ${C.border}`, color: C.text,
  borderRadius: 8, padding: "7px 11px", fontSize: 13, minWidth: 130,
};

export default function Mt5AuditLog() {
  const [logs, setLogs] = useState([]);
  const [events, setEvents] = useState([]);
  const [email, setEmail] = useState("");
  const [symbol, setSymbol] = useState("");
  const [event, setEvent] = useState("");
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mt5/admin/logs`, {
        params: { email, symbol, event, days, limit: 300 }, withCredentials: true,
      });
      setLogs(data.logs || []);
      if (data.events) setEvents(data.events);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load audit log");
    } finally { setLoading(false); }
  }, [email, symbol, event, days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: "8px 24px 40px" }} data-testid="mt5-audit-log">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ color: C.text, fontSize: 18, margin: 0 }}>MT5 Execution Audit Trail</h2>
        <span style={{ color: C.muted, fontSize: 12 }}>{logs.length} events</span>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
        <input data-testid="audit-filter-email" style={inp} placeholder="user email…" value={email} onChange={e => setEmail(e.target.value)} />
        <input data-testid="audit-filter-symbol" style={inp} placeholder="symbol (e.g. XAUUSD)" value={symbol} onChange={e => setSymbol(e.target.value)} />
        <select data-testid="audit-filter-event" style={inp} value={event} onChange={e => setEvent(e.target.value)}>
          <option value="">All events</option>
          {events.map(ev => <option key={ev} value={ev}>{ev}</option>)}
        </select>
        <select data-testid="audit-filter-days" style={inp} value={days} onChange={e => setDays(Number(e.target.value))}>
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button data-testid="audit-search-btn" onClick={load}
          style={{ background: C.gold, border: "none", color: "#07090f", borderRadius: 8, padding: "7px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Search
        </button>
      </div>

      {err && <div style={{ color: C.red }} data-testid="audit-error">{err}</div>}
      {loading ? (
        <div style={{ color: C.muted }} data-testid="audit-loading">Loading audit log…</div>
      ) : logs.length === 0 ? (
        <div style={{ color: C.muted }} data-testid="audit-empty">No matching audit events.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, color: C.text }} data-testid="audit-table">
            <thead>
              <tr style={{ color: C.muted, textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
                {["Time", "User", "Event", "Symbol", "Detail"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => {
                const d = l.detail || {};
                return (
                  <tr key={`${l.at}-${i}`} style={{ borderBottom: `1px solid ${C.border}` }} data-testid="audit-row">
                    <td style={{ padding: "8px 10px", color: C.muted, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                      {l.at ? new Date(l.at).toLocaleString() : "—"}
                    </td>
                    <td style={{ padding: "8px 10px" }}>{l.email || <span style={{ color: C.muted }}>{(l.user_id || "").slice(0, 8)}</span>}</td>
                    <td style={{ padding: "8px 10px" }}>
                      <span style={{ color: EVENT_COLOR[l.event] || C.text, fontFamily: "monospace", fontWeight: 700 }}>{l.event}</span>
                    </td>
                    <td style={{ padding: "8px 10px", fontFamily: "monospace" }}>{d.symbol || "—"}</td>
                    <td style={{ padding: "8px 10px", color: C.muted, fontFamily: "monospace", fontSize: 11.5 }}>
                      {[d.order_type && `${d.order_type}`, d.status && `status=${d.status}`, d.reason && `reason=${d.reason}`, d.source && `src=${d.source}`, d.ticket && `#${d.ticket}`, d.price != null && `@${d.price}`]
                        .filter(Boolean).join(" · ") || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
